/**
 * The Vault's Overview tab: six summary cards, the featured hero, the
 * recently-opened strip, search and category chips, and the pack grid.
 *
 * The hero and the pack cards use ArtworkPlaceholder with hueMultiplier 3, which is
 * what gives vault packs a different colour family from songs (multiplier 7) even
 * though both hash their id. That difference is intentional design, not an accident
 * of the numbers.
 */
import { Download, Eye, Package, Search, Star } from 'lucide-react';
import { Btn, Chip, IconButton, StatCard } from '../ui';
import { ArtworkPlaceholder } from '../songs';
import { packAssetCount } from '../../hooks';
import { c } from '../../styles/theme';
import type { VaultPack } from '../../types/domain';
import { Note, TRUNCATE, type VaultController } from './shared';

export interface VaultOverviewTabProps {
  vault: VaultController;
  /** Opens a pack in the detail drawer. */
  onOpenPack: (pack: VaultPack) => void;
}

export default function VaultOverviewTab({ vault, onOpenPack }: VaultOverviewTabProps) {
  if (!vault.heroPack) {
    return <Note marginBottom={0}>No vault packs are available yet.</Note>;
  }

  return (
    <>
      <div className="lyx-grid-2" style={{ marginBottom: 18 }}>
        <StatCard label="Music Packs" value={vault.totals.packs} />
        <StatCard label="Prompt Packs" value={vault.totals.prompts} />
        <StatCard label="Genre Presets" value={vault.totals.presets} />
        <StatCard label="Structures" value={vault.totals.structures} />
        <StatCard label="Hooks" value={vault.totals.hooks} />
        <StatCard
          label="Metadata + Marketing"
          value={vault.totals.metadata + vault.totals.marketing}
        />
      </div>

      {/* featured hero */}
      <ArtworkPlaceholder
        id={vault.heroPack.id}
        depth="packCard"
        saturation={[50, 45]}
        hueMultiplier={3}
        style={{
          borderRadius: 14,
          overflow: 'hidden',
          marginBottom: 20,
          padding: '22px 22px',
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: '#fff',
            background: 'rgba(0,0,0,0.35)',
            padding: '3px 10px',
            borderRadius: 999,
          }}
        >
          Featured Collection
        </span>
        <p
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 700,
            fontSize: 20,
            color: '#fff',
            margin: '10px 0 6px',
          }}
        >
          {vault.heroPack.name}
        </p>
        <p
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.75)',
            margin: '0 0 14px',
            maxWidth: 420,
          }}
        >
          {vault.heroPack.description}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {[
            `${vault.heroPack.prompts.length} Prompts`,
            `${vault.heroPack.presets.length} Presets`,
            `${vault.heroPack.structures.length} Structures`,
            `${vault.heroPack.hooks.length} Hooks`,
            `${vault.heroPack.metadata.length} Metadata`,
          ].map((t) => (
            <span
              key={t}
              style={{
                fontSize: 11,
                color: '#fff',
                background: 'rgba(0,0,0,0.3)',
                padding: '4px 10px',
                borderRadius: 999,
              }}
            >
              {t}
            </span>
          ))}
        </div>
        <Btn onClick={() => onOpenPack(vault.heroPack)}>
          <Eye size={13} />
          Open Collection
        </Btn>
      </ArtworkPlaceholder>

      {vault.recentPackList.length > 0 && (
        <>
          <Note>Recently opened</Note>
          <div
            style={{
              display: 'flex',
              gap: 10,
              overflowX: 'auto',
              marginBottom: 20,
              paddingBottom: 4,
            }}
          >
            {vault.recentPackList.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onOpenPack(p)}
                style={{
                  flex: '0 0 140px',
                  textAlign: 'left',
                  border: `1px solid ${c.border}`,
                  borderRadius: 10,
                  background: c.surface1,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  padding: 0,
                }}
              >
                <ArtworkPlaceholder id={p.id} height={60} depth="hero" hueMultiplier={3} />
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    margin: '6px 8px',
                    color: c.text1,
                    ...TRUNCATE,
                  }}
                >
                  {p.name}
                </p>
              </button>
            ))}
          </div>
        </>
      )}

      {/* search + category chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div
          style={{
            flex: '1 1 180px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: '6px 10px',
            background: c.surface1,
          }}
        >
          <Search size={13} color={c.text3} aria-hidden="true" />
          <input
            value={vault.search}
            onChange={(e) => vault.setSearch(e.target.value)}
            placeholder="Search packs, prompts, hooks, metadata…"
            // The prototype's search inputs had no label of any kind.
            aria-label="Search the vault"
            style={{
              border: 'none',
              background: 'transparent',
              color: c.text1,
              fontSize: 13,
              width: '100%',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        <Chip
          label="All"
          active={vault.categoryFilter === 'All'}
          onClick={() => vault.setCategoryFilter('All')}
        />
        {vault.categories.map((cat) => (
          <Chip
            key={cat.id}
            label={cat.name}
            active={vault.categoryFilter === cat.name}
            onClick={() => vault.setCategoryFilter(cat.name)}
          />
        ))}
      </div>

      {/* pack grid */}
      {vault.filteredPacks.length === 0 ? (
        <Note marginBottom={0}>No packs match your search or filter.</Note>
      ) : (
        <div className="lyx-song-grid">
          {vault.filteredPacks.map((p) => {
            const favorited = vault.favoritePacks.includes(p.id);
            return (
              <div
                key={p.id}
                style={{
                  border: `1px solid ${c.border}`,
                  borderRadius: 12,
                  background: c.surface1,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={() => onOpenPack(p)}
                  aria-label={`Open ${p.name}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    background: 'none',
                  }}
                >
                  <ArtworkPlaceholder id={p.id} height={84} depth="packCard" hueMultiplier={3}>
                    <Package
                      size={20}
                      color="rgba(255,255,255,0.5)"
                      style={{ position: 'absolute', bottom: 8, left: 10 }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        fontSize: 10,
                        color: '#fff',
                        background: 'rgba(0,0,0,0.4)',
                        padding: '2px 8px',
                        borderRadius: 999,
                      }}
                    >
                      {p.category}
                    </span>
                  </ArtworkPlaceholder>
                </button>
                <div style={{ padding: '10px 12px' }}>
                  <p style={{ fontWeight: 500, fontSize: 13, margin: '0 0 2px', ...TRUNCATE }}>
                    {p.name}
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: c.text3,
                      margin: '0 0 6px',
                      ...TRUNCATE,
                    }}
                  >
                    {p.description}
                  </p>
                  <p style={{ fontSize: 10, color: c.text3, margin: '0 0 8px' }}>
                    {packAssetCount(p)} assets · Updated {p.updated}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Btn onClick={() => onOpenPack(p)} style={{ flex: 1 }}>
                      <Eye size={12} />
                      Open
                    </Btn>
                    <IconButton
                      label={
                        favorited ? `Remove ${p.name} from favorites` : `Add ${p.name} to favorites`
                      }
                      onClick={() => vault.togglePackFavorite(p.id)}
                    >
                      <Star
                        size={15}
                        color={favorited ? c.accent : c.text3}
                        fill={favorited ? c.accent : 'none'}
                      />
                    </IconButton>
                    <IconButton label={`Download ${p.name}`} onClick={() => vault.downloadPack(p)}>
                      <Download size={15} color={c.text3} />
                    </IconButton>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
